<template>
  <div class="checkout-total">
    <h3>Общая стоимость</h3>

    <checkout-total-group
      v-for="group in groups"
      :key="group.id"
      ref="group"
      :group="group"
      :loading="isLoaded"
    />

    <div class="checkout-total-final">
      <div>Всего к оплате</div>
      <div class="price-rub">
        {{ total | price }}
      </div>
    </div>

    <ui-button
      :loading="loading"
      :text="submitText"
      wide
      large
      class="checkout-total__submit"
      @click="$emit('submit')"
    />

    <div class="checkout-total__agreement">
      Нажимая на кнопку «Оформить заказ» вы принимаете условия <a target="_blank" href="/agreement">пользовательского соглашения</a> и
      <a target="_blank" href="/public_ofert">публичной оферты</a>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
/* Utils */
import { pluralize } from '@/utils/string-utils';
import { ddmmyyyy } from '@/utils/date-to-string';
import isToday from '@/utils/is-today';
import isTomorrow from '@/utils/is-tomorrow';
/* Mixins */
import getIntervalDates from '@/mixins/get-interval-dates';
/* Components */
import CheckoutTotalGroup from './checkout-total-group.vue';

export default {
  name: 'checkout-total',

  components: {
    CheckoutTotalGroup
  },

  mixins:[getIntervalDates],

  props: {
    loading: {
      type: Boolean,
      default: false,
    }
  },

  data() {
    return {
      isLoaded: false
    }
  },

  computed: {
    ...mapGetters({
      SHIPPING_GROUPS: 'cart/shippingGroups',
      SUBTOTAL: 'cart/subtotal',
      DELIVERY_ADDRESS: 'cart/deliveryAddress',
      PICKUP_STORES: 'cart/pickupStores',
      THIRDPARTY_STORES: 'cart/thirdpartyStores',
      SELECTED_DATE: 'cart/deliverySelectedDate',
    }),

    shippingGroups() {
      return this.SHIPPING_GROUPS
        .filter(group => group.type !== 'soldout'); // убираем распроданное
    },

    groups() {
      return this.shippingGroups.map((group, i) => {
        const productsCount = group.products.reduce((a, { lineIds = [] } = {}) => a + lineIds.length, 0);
        const hasInterval = group?.receivedInterval?.from && group?.receivedInterval?.to;
        const dateText = hasInterval
          ? this.getIntervalDates(group.receivedInterval.from, group.receivedInterval.to)
          : this.getVerbalDate(group.receivedOn);

        return {
          id: group.id,
          order: i + 1,
          type: group.type || '',
          subtotal: group.subtotal || {},
          productsCount,
          discounts: (() => {
            const discounts = group.subtotal?.discounts?.filter(d => ['ware_discount', 'promotion', 'promocode', 'bonus'].includes(d.type)) || [];
            return discounts.map((d) => ({
                name: (() => {
                  switch (d.type) {
                    case 'ware_discount': return 'Скидка FUNDAY';
                    case 'promotion': return 'Скидка ' + d.name;
                    case 'promocode': return 'Промокод ' + d.name;
                    case 'bonus': return 'Бонусные баллы';
                    default: return 'Скидка';
                  }
                })(),
                sum: d.value,
              }
            ));
          })(),
          title: (() => {
            switch (group.type) {
              case 'pickup': return `Забрать ${pluralize(productsCount, ['товар', 'товара', 'товаров'])} из магазина <span>${dateText}</span>`;
              case 'thirdparty': return `Забрать  ${pluralize(productsCount, ['товар', 'товара', 'товаров'])} в пункте самовывоза <span>${dateText}</span>`;
              case 'delivery': return `Доставить ${pluralize(productsCount, ['товар', 'товара', 'товаров'])} курьером <span>${dateText}</span> ${this.parseTimeInterval(this.SELECTED_DATE.interval)}`;
              case 'unallocated': return `Не выбран способ получения для ${pluralize(productsCount, ['товара', 'товаров', 'товаров'])}`;
              default: return '';
            }
          })(),
          subtitle: (() => {
            switch (group.type) {
              case 'pickup':
                return this.PICKUP_STORES[group.storeId]
                  ? `${this.PICKUP_STORES[group.storeId].name}, ${this.PICKUP_STORES[group.storeId].address}`
                  : '';
              case 'thirdparty':
                return this.THIRDPARTY_STORES[group.thirdPartyId]
                  ? `${this.THIRDPARTY_STORES[group.thirdPartyId].name}, ${this.THIRDPARTY_STORES[group.thirdPartyId].address}`
                  : '';
              case 'delivery':
                return this.DELIVERY_ADDRESS || 'Не указан адрес доставки';
              default:
                return this.getSubtitle(productsCount, group.shippingChannels);
            }
          })(),
        };
      });
    },

    total() {
      return Number(this.SUBTOTAL || 0);
    },

    submitText() {
      const count = this.shippingGroups.length;
      return `Оформить ${pluralize(count, ['заказ', 'заказа', 'заказов'], count < 2)}`;
    },
  },

  mounted() {
    this.isLoaded = true;
  },

  methods: {
    parseTimeInterval(interval) {
      if (typeof interval !== 'object' && !interval.length) return '';

      const [from, to] = interval.split(' - ');
      return `c ${from} до ${to}`;
    },

    getSubtitle(products, shippingChannels) {
      if (shippingChannels.some(c => !c.available)) {
        let items = products > 1 ? 'Товары доступны' : 'Товар доступен';
        let method = shippingChannels.some(c => c.type === 'delivery' && c.available) ? 'доставки курьером' : 'самовывоза';

        return `${items} только для ${method}`;
      }
      return 'Выберите удобный способ получения';
    },

    getVerbalDate(date) {
      return isToday(new Date(date)) ? 'сегодня' : isTomorrow(new Date(date)) ? 'завтра' : ddmmyyyy(new Date(date));
    },
  }
}
</script>

<style lang="scss">
.checkout-total {
  width: 100%;

  &-final {
    display: flex;
    padding: 20px 0 0;

    > * {
      font-size: 16px;
      line-height: 24px;
      font-weight: 600;

      &:last-child {
        font-size: 20px;
        margin-left: auto;
      }
    }
  }

  &__submit {
    margin-top: 12px;
  }

  &__agreement {
    margin-top: 16px;
    font-size: 12px;
    line-height: 20px;
    color: $gray;

    & > a {
      color: $black;
    }
  }
}

@include breakpoint("desktop") {
  .checkout-total {
    &-final {
      padding: 26px 0 0;
    }

    &__submit {
      margin-top: 8px;
    }

    &__agreement {
      margin-top: 24px;
      font-size: 14px;
      line-height: 24px;
    }
  }
}
</style>
